/**
 * Shared MIPS query hooks (frontend-snappiness plan, Phase 1a).
 *
 * One query per resource per (practice, year[, evaluation context]) across the
 * whole app — pages that used to fetch weights/scores/summaries independently
 * (Summary header, InsightsGrid, PaymentAdjustmentReport, PracticeOverview)
 * now share a cache entry and revalidate in the background.
 *
 * Rules (locked in canvases/frontend-snappiness-plan.md):
 * - Entity-guarded keepPreviousData on every year/npi/eval-keyed query —
 *   a year/NPI switch for the SAME practice holds last-good data (no skeleton
 *   flash), but switching PRACTICE must not paint the previous practice's
 *   numbers (2026-08-03 fix: unguarded keepPreviousData caused visible
 *   content swaps when the entity changed).
 * - Skeletons render only on `!data`, never on `isFetching`.
 * - All fetching goes through apiClient (global 401 handling).
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { qk, keepPreviousSameEntity } from '@/lib/query-keys';
import { getIAScore } from '@/lib/ia-calculator-utils';
import { getCostDashboardData } from '@/lib/cost-calculator-utils';
import { composeCategoryScores, compositeOf, type MipsCategoryScores } from '@/lib/scenario/categories';

export interface MipsWeightsResponse {
  quality_weight?: number | string;
  pi_weight?: number | string;
  ia_weight?: number | string;
  cost_weight?: number | string;
  use_modeled_pi?: boolean;
  use_modeled_ia?: boolean;
  use_modeled_cost?: boolean;
  modeled_pi_percent?: number;
  modeled_ia_percent?: number;
  modeled_cost_percent?: number;
}

export interface MipsScoresResponse {
  quality_score?: number | string;
  pi_score?: number | string;
  ia_score?: number | string;
  cost_score?: number | string;
  patient_complexity_bonus?: number | string;
  composite_mips_score?: number | string;
  calculation_method?: string;
  pi_score_source?: string;
  ia_score_source?: string;
}

/** Visible MIPS practices for the signed-in user (Data Management banner/history). */
export function useUserPractices() {
  return useQuery<any[]>({
    queryKey: qk.userPractices(),
    queryFn: () => apiClient.get('/api/user/practices'),
    // User-scoped list — hold across navigations; cleared on logout.
    placeholderData: (prev) => prev,
    staleTime: 5 * 60_000,
  });
}

export function useMipsWeights(practiceId: number | string | undefined | null, year: number) {
  return useQuery<MipsWeightsResponse>({
    queryKey: qk.weights(practiceId ?? '', year),
    queryFn: () => apiClient.get(`/api/practices/${practiceId}/mips/weights?year=${year}`),
    enabled: !!practiceId,
    // key: ['mips','weights', practiceId, year] — entity at index 2
    placeholderData: keepPreviousSameEntity<MipsWeightsResponse>(2, String(practiceId ?? '')),
  });
}

export function useMipsScores(practiceId: number | string | undefined | null, year: number) {
  return useQuery<MipsScoresResponse>({
    queryKey: qk.scores(practiceId ?? '', year),
    queryFn: () => apiClient.get(`/api/practices/${practiceId}/mips/scores?year=${year}`),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity<MipsScoresResponse>(2, String(practiceId ?? '')),
  });
}

/** Practice settings (small-practice flag, special statuses) — same cache entry as Configuration/MVP screens. */
export function usePracticeSettings(practiceId: number | string | undefined | null, year: number) {
  const id = practiceId != null ? String(practiceId) : '';
  return useQuery<any>({
    queryKey: qk.practiceSettings(id || 'none', year),
    queryFn: () => apiClient.get(`/api/practices/${practiceId}/settings?year=${year}`),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity(2, id),
  });
}

export function usePerformanceSummaries(
  practiceId: number | string | undefined | null,
  year: number,
  evaluationType: string = 'GROUP',
  npi?: string | null,
  enabled: boolean = true,
) {
  return useQuery<any[]>({
    queryKey: qk.summaries(practiceId ?? '', year, evaluationType, npi),
    queryFn: () => {
      const npiQ = npi ? `&npi=${encodeURIComponent(npi)}` : '';
      return apiClient.get(
        `/api/performance/${practiceId}/summaries?year=${year}&evaluationType=${evaluationType}${npiQ}`,
      );
    },
    enabled: !!practiceId && enabled,
    // /summaries is the heaviest GET in the app; allow a longer stale window
    // (matches the 5-min measure-outcomes TTL philosophy, conservative at 2m).
    staleTime: 2 * 60_000,
    // key: ['performance','summaries', practiceId, ...] — entity at index 2
    placeholderData: keepPreviousSameEntity<any[]>(2, String(practiceId ?? '')),
  });
}

export function useIaScore(
  practiceId: number | string | undefined | null,
  year: number,
  npi?: string | null,
  enabled: boolean = true,
) {
  return useQuery({
    // getIAScore returns null on any failure (never throws) — parity with the
    // old per-component try/catch-to-zero behavior is automatic.
    queryKey: qk.iaScore(practiceId ?? '', year, npi),
    queryFn: () => getIAScore(String(practiceId), { year, npi: npi || undefined }),
    enabled: !!practiceId && enabled,
    // key: ['ia','score', practiceId, ...] — entity at index 2
    placeholderData: keepPreviousSameEntity(2, String(practiceId ?? '')),
  });
}

export function useCostDashboard(
  practiceId: number | string | undefined | null,
  year: number,
  npi?: string | null,
  enabled: boolean = true,
) {
  return useQuery({
    // getCostDashboardData returns null on any failure (never throws).
    queryKey: qk.costDashboard(practiceId ?? '', year, npi),
    queryFn: () => getCostDashboardData(String(practiceId), { year, npi: npi || undefined }),
    enabled: !!practiceId && enabled,
    // key: ['cost','dashboard', practiceId, ...] — entity at index 2
    placeholderData: keepPreviousSameEntity(2, String(practiceId ?? '')),
  });
}

export type { MipsCategoryScores };

/**
 * The composite-building block shared by InsightsGrid, PaymentAdjustmentReport
 * (and any future consumer): weights + scores in parallel, then IA/Cost
 * enrichments only when that category is NOT modeled. Identical math to the
 * Gap Management header. Composite = sum of the five parts.
 */
export function useMipsCategoryScores(
  practiceId: number | string | undefined | null,
  year: number,
) {
  const weightsQ = useMipsWeights(practiceId, year);
  const scoresQ = useMipsScores(practiceId, year);

  const w = weightsQ.data;
  const useModeledIa = w?.use_modeled_ia === true;
  const useModeledCost = w?.use_modeled_cost === true;

  // Enrichments fire once weights are known and the category is calculated
  // (not modeled). GROUP context (no NPI) — matches today's Insights usage.
  const iaQ = useIaScore(practiceId, year, null, !!w && !useModeledIa);
  const costQ = useCostDashboard(practiceId, year, null, !!w && !useModeledCost);

  const loading =
    weightsQ.isPending ||
    scoresQ.isPending ||
    (!!w && !useModeledIa && iaQ.isPending) ||
    (!!w && !useModeledCost && costQ.isPending);

  const categoryScores: MipsCategoryScores = composeCategoryScores({
    weights: w,
    scores: scoresQ.data,
    iaData: iaQ.data,
    costData: costQ.data,
  });
  const compositeScore = compositeOf(categoryScores);

  return { categoryScores, compositeScore, loading };
}

export function usePaymentAdjustmentConfig(year: number) {
  return useQuery<any>({
    queryKey: qk.paymentAdjConfig(year),
    // null = config missing/unavailable for this year (consumers show their
    // "not configured — contact your administrator" state, matching the old
    // explicit 404 handling).
    queryFn: () =>
      apiClient.get(`/api/admin/payment-adjustment-config/${year}`).catch(() => null),
    // Global per-year constants — safe to hold across any switch.
    placeholderData: (prev: any) => prev,
    // Admin-configured constants; change rarely.
    staleTime: 10 * 60_000,
  });
}

export function usePracticeEligibility(
  practiceId: number | string | undefined | null,
  year: number,
) {
  return useQuery<any>({
    queryKey: qk.eligibility(practiceId ?? '', year),
    // null = no eligibility import (consumers already handle the "upload your
    // QPP eligibility file" empty state).
    queryFn: () =>
      apiClient.get(`/api/practices/${practiceId}/eligibility/${year}`).catch(() => null),
    enabled: !!practiceId,
    // key: ['eligibility', practiceId, year] — entity at index 1
    placeholderData: keepPreviousSameEntity(1, String(practiceId ?? '')),
    staleTime: 10 * 60_000,
  });
}

/**
 * Per-clinician eligibility (NPI view of the score card). Shares the
 * ['eligibility', practiceId] prefix so invalidatePracticeScoring reaches it.
 * null = not imported / not found (consumers show their import prompt).
 */
export function usePracticeEligibilityNpi(
  practiceId: number | string | undefined | null,
  year: number,
  npi?: string | null,
) {
  return useQuery<any>({
    queryKey: ['eligibility', String(practiceId ?? ''), year, 'npi', npi ?? null],
    queryFn: () =>
      apiClient
        .get(`/api/practices/${practiceId}/eligibility/${year}/npi/${npi}`)
        .catch(() => null),
    enabled: !!practiceId && !!npi,
    placeholderData: keepPreviousSameEntity(1, String(practiceId ?? '')),
    staleTime: 10 * 60_000,
  });
}

/** APP Plus Actions card goals (QPS). Prefetch via useAppPlusScoring too. */
export function useAcoGoals(
  acoId: number | string | undefined | null,
  year: number,
) {
  return useQuery<any[]>({
    queryKey: qk.acoGoals(acoId ?? '', year),
    queryFn: async () => {
      const data = await apiClient.get(`/api/acos/${acoId}/goals?year=${year}`);
      return Array.isArray(data) ? data : [];
    },
    enabled: !!acoId,
    // key: ['aco', acoId, 'goals', year] — entity at index 1
    placeholderData: keepPreviousSameEntity(1, String(acoId ?? '')),
  });
}

/** APP Plus roster rollup (Insights card + CMS Reports + Roster report). */
export function useAcoRosterDerived(
  acoId: number | string | undefined | null,
  year: number,
) {
  return useQuery<any>({
    queryKey: qk.acoRosterDerived(acoId ?? '', year),
    queryFn: () => apiClient.get(`/api/acos/${acoId}/roster/derived?year=${year}`),
    enabled: !!acoId,
    // key: ['aco', acoId, 'roster-derived', year] — entity at index 1
    placeholderData: keepPreviousSameEntity(1, String(acoId ?? '')),
  });
}

/** One measure's counts for a TIN or NPI row (read-time roll-up over the NPI lane). */
export interface TinNpiMeasureCounts {
  measureId: string;
  submissionMethod: string;
  stratum: string;
  ip: number; ex: number; den: number; met: number; exc: number; not: number;
  patients: number;
}
export interface TinPerformanceRow {
  tin: string;
  name: string | null;
  npiCount: number;
  patients: number;
  measures: TinNpiMeasureCounts[];
}
export interface NpiPerformanceRow {
  npi: string;
  tin: string;
  name: string | null;
  qpStatus: string | null;
  mustReportClass: string | null;
  mipsEligible: boolean | null;
  patients: number;
  measures: TinNpiMeasureCounts[];
}
export interface TinNpiPerformanceResponse {
  programYear: number;
  shadowPracticeId: number | null;
  submissionMethod: string | null;
  rosterImported: boolean;
  tins: TinPerformanceRow[];
  npis: NpiPerformanceRow[];
  rosterOnly: Array<{ npi: string; tin: string; name: string | null; qpStatus: string | null; mustReportClass: string | null }>;
}

/** APP Plus TIN & NPI performance report (Insights). */
export function useAcoTinNpiPerformance(
  acoId: number | string | undefined | null,
  year: number,
  submissionMethod: string | null,
  enabled: boolean = true,
) {
  return useQuery<TinNpiPerformanceResponse>({
    queryKey: qk.acoTinNpiPerformance(acoId ?? '', year, submissionMethod),
    queryFn: () => apiClient.get(
      `/api/acos/${acoId}/performance/tin-npi?year=${year}${
        submissionMethod ? `&submissionMethod=${encodeURIComponent(submissionMethod)}` : ''
      }`,
    ),
    enabled: !!acoId && enabled,
    staleTime: 2 * 60_000,
    placeholderData: keepPreviousSameEntity<TinNpiPerformanceResponse>(1, String(acoId ?? '')),
  });
}

export function useAcoRoster(
  acoId: number | string | undefined | null,
  year: number,
) {
  return useQuery<any>({
    queryKey: qk.acoRoster(acoId ?? '', year),
    queryFn: () => apiClient.get(`/api/acos/${acoId}/roster?year=${year}`),
    enabled: !!acoId,
    placeholderData: keepPreviousSameEntity(1, String(acoId ?? '')),
  });
}

export function useAcoParticipants(
  acoId: number | string | undefined | null,
  year: number,
) {
  return useQuery<any>({
    queryKey: qk.acoParticipants(acoId ?? '', year),
    queryFn: () => apiClient.get(`/api/acos/${acoId}/participants?year=${year}`),
    enabled: !!acoId,
    placeholderData: keepPreviousSameEntity(1, String(acoId ?? '')),
  });
}
